// @flow
import React, {useEffect, useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {useForm} from 'react-hook-form';
import {makeStyles} from '@material-ui/core/styles';
import {
  Typography,
  Box,
  IconButton,
  Divider,
  InputAdornment,
  TextField,
  ClickAwayListener,
} from '@material-ui/core';
import {Add, DeleteOutlined} from '@material-ui/icons';

import {colors} from '../../../../assets/styles/colors';
import CustomModal from '../../../../elements/CustomModal/CustomModal';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import ColorsPalette from '../ColorsPalette/ColorsPalette';
import {getPastelColor} from '../../../../utils/style';
import withoutColorIcon from '../../../../assets/img/without-color.svg';

import type {CategoryRequest} from '../../api';
import type {CustomerCategory} from '../../../../entities';

type Props = {|
  open: boolean,
  isSaving?: boolean,
  handleClose: () => void,
  categories?: CustomerCategory[],
  createCategory: ({categoryRequest: CategoryRequest}) => void,
  editCategory: ({
    categoryRequest: CategoryRequest,
    categoryId: string,
  }) => void,
  deleteCategory: ({categoryRequest: CategoryRequest}) => void,
  createCategorySuccess?: boolean,
  editCategorySuccess?: boolean,
  deleteCategorySuccess?: boolean,
|};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyItems: 'center',
    alignItems: 'center',
    textAlign: 'center',
    // width: 320,
    // height: 240,
    padding: theme.spacing(4, 0),
  },
  content: {
    maxWidth: 500,
    width: '100%',
    margin: 'auto',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.375rem',
  },
  subtitle: {
    textAlign: 'start',
    fontSize: '0.875rem',
    color: colors.grey700,
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  dividerVertical: {
    height: 'auto',
    margin: theme.spacing(0, 2),
    backgroundColor: '#E8E8ED',
  },
  sectionTitle: {
    fontWeight: 600,
    textAlign: 'start',
  },
  categoryRow: {
    position: 'relative',
    marginTop: theme.spacing(1),
    display: 'flex',
  },
  categoryTextField: {
    flexGrow: 1,
    '& .MuiFilledInput-input': {
      fontSize: '14px!important',
    },
  },
  categoryButton: {
    minWidth: 96,
    width: 96,
    marginLeft: theme.spacing(1),
    borderRadius: '8px',
    backgroundColor: colors.grey200,
    boxShadow: 'none',
  },
  addButton: {
    padding: theme.spacing(1.75, 1),
    backgroundColor: '#F5F0FF',
    '&:hover': {
      backgroundColor: '#F5F0FF',
    },
  },
  categoriesArray: {
    overflowY: 'auto',
    // marginBottom: theme.spacing(2),
  },
  deleteButton: {
    marginLeft: 1,
    borderRadius: '8px',
    color: theme.palette.error.main,
  },
  orderButton: {
    borderRadius: '8px',
    padding: 'unset',
    flex: 1,
    border: 'unset',
    backgroundColor: colors.grey200,
    '&:hover': {
      backgroundColor: colors.grey300,
      boxShadow: 'none',
    },
    '&:disabled': {
      border: 'unset',
    },
  },
  confirmButton: {
    marginTop: theme.spacing(3),
    width: 280,
    height: 40,
    // color:'white',
    fontWeight: 600,
    fontSize: '1.125rem',
  },
  inputAdornment: {
    padding: '14px !important',
  },
  singleCategory: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: theme.spacing(1, 0),
    boxShadow: 'inset 0px 1px 4px rgba(140, 139, 163, 0.12)',
    borderRadius: '8px',
    width: '100%',
    padding: '0 0 2px 16px',
  },
  singleCategoryName: {
    textAlign: 'left',
    fontSize: '14px',
    color: colors.grey800,
  },
  customTextField: {
    padding: 0,
    flexWrap: 'nowrap',
    borderRadius: '8px',
    border: `1.25px solid ${colors.grey300}`,
    boxSizing: 'border-box',
    background: 'inherit!important',
    color: colors.grey800,
    '& .MuiFilledInput-input': {
      padding: '15px',
    },
    '& .MuiInputBase-input.Mui-disabled': {
      backgroundColor: colors.grey200,
    },
    '&.Mui-focused': {
      border: `1px solid ${colors.grey500} `,
      backgroundColor: 'white ',
      color: `${colors.grey800} `,
    },
  },
  textInput: {
    borderRadius: '8px',
    // fontSize: '0.875rem',
    color: colors.grey800,
  },
  textInputLabel: {
    color: colors.grey600,
  },
  containerPalette: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  circle: {
    zIndex: 3,
    cursor: 'pointer',
    width: '1.5rem',
    height: '1.5rem',
    borderRadius: '50%',
  },
  floatPalette: {
    zIndex: '6',
    position: 'absolute',
    top: -16,
    left: 0,
  },
  rowCategory: {
    position: 'relative',
    display: 'flex',
    // alignItems: 'center',
  },
  customDragItem: {
    position: 'relative',
    cursor: 'pointer',
  },
  categoryRowInput: {
    display: 'flex',
    alignItems: 'center',
  },
  selectPaletteBtn: {
    margin: theme.spacing(0, 0.5),
  },
  iconSelectPallette: {
    padding: '0!important',
  },
  defaultPalette: {cursor: 'pointer', width: '1.5rem', height: '1.5rem'},
}));

const paletteColorsOpt = ['#51D08B', '#F45C80', '#4E4E73', '#2F8EDF'];

export default function CustomerCategoryModal(props: Props) {
  const {
    open,
    isSaving,
    handleClose,
    categories,
    createCategory,
    deleteCategory,
    editCategory,
    createCategorySuccess,
    editCategorySuccess,
    deleteCategorySuccess,
  } = props;

  const classes = useStyles();
  const {register, handleSubmit, errors, setValue, reset, watch} = useForm();
  const [localCategories, setLocalCategories] = useState([]);
  const [openPalette, setOpenPalette] = useState();
  const [paletteColorSelected, setPaletteColorSelected] = useState();
  const [editingCategory, setEditingCategory] = useState(-1);

  const {newCategory} = watch();

  useEffect(() => {
    if (categories) {
      setLocalCategories(categories);
    }
  }, [categories]);

  useEffect(() => {
    if (createCategorySuccess || editCategorySuccess || deleteCategorySuccess) {
      setOpenPalette(false);
      setEditingCategory(-1);
      setPaletteColorSelected();
      setValue('newCategory', '');
    }
  }, [
    createCategorySuccess,
    deleteCategorySuccess,
    editCategorySuccess,
    setValue,
  ]);

  const handleDeleteCategory = (id: string) => {
    deleteCategory({categoryRequest: {id}});
    setEditingCategory(-1);
  };

  const handleAddCategorySubmit = data => {
    if (data.newCategory) {
      createCategory({
        categoryRequest: {name: data.newCategory, color: paletteColorSelected},
      });
      reset();
      setPaletteColorSelected();
    }
  };

  const handleEditCategory = (newName: string, category: CustomerCategory) => {
    if (newName !== category.name) {
      editCategory({
        categoryRequest: {name: newName},
        categoryId: category.id,
      });
    }
    setEditingCategory(-1);
  };
  const handleOpenPalette = () => {
    setOpenPalette(true);
  };

  const onSelectPalette = (color: string, category: CustomerCategory) => {
    if (color !== category.color) {
      editCategory({
        categoryRequest: {color},
        categoryId: category.id,
      });
    }
    setEditingCategory(-1);
  };

  const getDefaultPalette = useCallback(
    (c: CustomerCategory) => (
      <div
        className={c && c.color ? classes.circle : classes.defaultPalette}
        onClick={handleOpenPalette}
        style={{
          backgroundColor: c.color ? c.color : undefined,
        }}
      >
        {c && !c.color ? (
          <img
            src={withoutColorIcon}
            alt="without color"
            style={{maxWidth: '100%'}}
          />
        ) : null}
      </div>
    ),
    [classes.circle, classes.defaultPalette]
  );

  const categoriesArray = localCategories
    ? localCategories.map((c: CustomerCategory, index: number) => (
        <div key={`customer-category-${index}`}>
          {editingCategory === index ? (
            <div className={classes.categoryRow}>
              {openPalette && (
                <div className={classes.floatPalette}>
                  <ClickAwayListener onClickAway={() => setOpenPalette(false)}>
                    <div className={classes.containerPalette}>
                      <ColorsPalette
                        paletteColorsOpt={paletteColorsOpt}
                        paletteColorSelected={paletteColorSelected}
                        setPaletteColorSelected={setPaletteColorSelected}
                        onSelect={cat => onSelectPalette(cat, c)}
                      />
                    </div>
                  </ClickAwayListener>
                </div>
              )}
              <TextField
                style={{backgroundColor: getPastelColor(c.color)}}
                autoFocus
                className={classes.categoryTextField}
                variant="filled"
                fullWidth
                defaultValue={c.name}
                onKeyPress={e => {
                  e.stopPropagation();
                  if (e.key === 'Enter') handleEditCategory(e.target.value, c);
                  if (e.key === 'Escape') setEditingCategory(-1);
                }}
                onBlur={
                  !openPalette
                    ? e => handleEditCategory(e.target.value, c)
                    : null
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment>
                      <IconButton
                        className={classes.deleteButton}
                        onClick={() => handleDeleteCategory(c.id)}
                      >
                        <DeleteOutlined />
                      </IconButton>
                    </InputAdornment>
                  ),
                  startAdornment: (
                    <InputAdornment>
                      <IconButton
                        className={classes.selectPaletteBtn}
                        onClick={handleOpenPalette}
                      >
                        {getDefaultPalette(c)}
                      </IconButton>
                    </InputAdornment>
                  ),
                  className: classes.customTextField,
                  disableUnderline: true,
                }}
              />
            </div>
          ) : (
            <div
              className={classes.singleCategory}
              style={{backgroundColor: getPastelColor(c.color)}}
              onClick={() => setEditingCategory(index)}
            >
              <div className={classes.rowCategory}>
                {getDefaultPalette(c)}
                <Divider
                  orientation="vertical"
                  className={classes.dividerVertical}
                />
                <Typography className={classes.singleCategoryName}>
                  {c.name}
                </Typography>
              </div>
              <div>
                <IconButton
                  className={classes.deleteButton}
                  onClick={() => handleDeleteCategory(c.id)}
                >
                  <DeleteOutlined />
                </IconButton>
              </div>
            </div>
          )}
        </div>
      ))
    : null;

  return (
    <CustomModal open={open} handleCloseModal={handleClose}>
      <div className={classes.root}>
        <Box className={classes.content}>
          <Typography align="center" gutterBottom className={classes.title}>
            Categoría de clientes
          </Typography>
          <Typography align="center" className={classes.subtitle}>
            Las categorías son importantes para ordenar los productos de tu
            tienda y así tus clientes puedan encontrar lo que buscan fácilmente.
          </Typography>
          <Divider className={classes.divider} />
          <Typography className={classes.sectionTitle} paragraph>
            Nombre de la categoría:
          </Typography>
          <div className={classes.categoryRow}>
            <CustomTextField
              className={classes.categoryTextField}
              label="Agrega nueva categoría"
              name="newCategory"
              error={!!errors.newCategory}
              helperText={errors.newCategory ? errors.newCategory.message : ''}
              inputRef={register({
                pattern: {
                  value: /^[A-Za-z]+$/,
                  message: 'Campo obligatorio',
                },
              })}
              onKeyPress={e => {
                if (e.key === 'Enter') handleSubmit(handleAddCategorySubmit)();
              }}
              onClick={() => setEditingCategory(-1)}
              type="text"
              disabled={isSaving}
            />
            <CustomButton
              variant="contained"
              color="primary"
              onClick={handleSubmit(handleAddCategorySubmit)}
              disabled={isSaving}
              className={`${classes.categoryButton} ${classes.addButton}`}
            >
              <Add color="primary" />
            </CustomButton>
          </div>
          {newCategory && newCategory.length > 0 ? (
            <div className={classes.containerPalette}>
              <ColorsPalette
                paletteColorsOpt={paletteColorsOpt}
                paletteColorSelected={paletteColorSelected}
                setPaletteColorSelected={setPaletteColorSelected}
              />
            </div>
          ) : null}
          {categoriesArray && (
            <Box className={classes.categoriesArray}>{categoriesArray}</Box>
          )}
        </Box>
      </div>
    </CustomModal>
  );
}

CustomerCategoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  categories: PropTypes.array,
  createCategory: PropTypes.func.isRequired,
  editCategory: PropTypes.func.isRequired,
  deleteCategory: PropTypes.func.isRequired,
  createCategorySuccess: PropTypes.bool,
  editCategorySuccess: PropTypes.bool,
  deleteCategorySuccess: PropTypes.bool,
};
